import { createEasyWebWorker } from "easy-web-worker";
import type {
  InferenceType,
  ProductInferenceAttributes,
} from "@/inference/types/inference";
import type { SectionInferenceAttributes } from "@/inference/types/section";

interface UIInferencePayload {
  sectionInfo: Record<string, SectionInferenceAttributes>;
  sectionScores: Record<string, number>;
  currentInference: InferenceType | null;
  flattenedScoreTable: Record<string, number>;
  categorySpecificScoreFlattened: Record<string, Record<string, number>>;
}

interface UIInferenceResponse {
  inference: InferenceType | null;
  topInferredAttributes: ProductInferenceAttributes;
  categorySpecificScore: Record<string, SectionInferenceAttributes>;
  categoryScore: Record<string, Record<string, number>>;
}

export const backgroundWorker = createEasyWebWorker<
  UIInferencePayload,
  UIInferenceResponse
>(({ onMessage }) => {
  function unflatten(data: any) {
    const result = {};

    for (const i in data) {
      if (
        [
          "maintenance_instructions",
          "installation_instructions",
          "weight",
        ].some((e) => i.includes(e))
      ) {
        continue;
      }

      const keys = i.split("~").map((e) => e.trim());
      keys.reduce((acc, key, idx) => {
        try {
          return (
            acc[key] || (acc[key] = idx === keys.length - 1 ? data[i] : {})
          );
        } catch (e) {
          console.log(
            {
              acc,
              key,
              i,
              keys,
              arr: [
                "maintenance_instructions",
                "installation_instructions",
                "weight",
              ],
            },
            "ERRORED IN VIEWTIME ",
          );
          return acc;
        }
      }, result);
    }

    return result;
  }
  const getScoreTable = (
    sectionInfo: SectionInferenceAttributes,
    score: number,
    flattenedScoreTable: Record<string, number> = {},
    categorySpecificScoreFlattened: Record<string, Record<string, number>> = {},
  ) => {
    function flattenObjectWithScoreValues(obj: Record<string, unknown>) {
      if (Array.isArray(obj)) {
        return Object.fromEntries(obj.map((e) => [e, score]));
      }
      const flattened: Record<string, number> = {};
      for (const key in obj) {
        const value = obj[key];
        if (typeof value === "object" && value !== null) {
          const flattenedValue = flattenObjectWithScoreValues(value);
          for (const subKey in flattenedValue) {
            flattened[`${key}~${subKey}`] = flattenedValue[subKey];
          }
        } else {
          flattened[`${key}~${value}`.toLowerCase()] = score;
        }
      }
      return flattened;
    }

    const subcategory = (
      sectionInfo?.sectionInferenceAttributes?.subcategory || "unknown"
    ).toLowerCase();
    const categoryScore = categorySpecificScoreFlattened[subcategory] || {};
    const sectionScore = flattenObjectWithScoreValues(sectionInfo);
    for (const flattenedKey in sectionScore) {
      flattenedScoreTable[flattenedKey] =
        (flattenedScoreTable[flattenedKey] ?? 0) + score;
      categoryScore[flattenedKey] = (categoryScore[flattenedKey] ?? 0) + score;
    }
    function unflatten(
      data: Record<string, number>,
      removeNullish: boolean = false,
    ) {
      if (removeNullish) {
        for (const key in data) {
          if (data[key] === 0) {
            delete data[key];
          }
        }
      }

      const result: Record<string, unknown> = {};

      for (const i in data) {
        if (
          [
            "maintenance_instructions",
            "installation_instructions",
            "weight",
          ].some((e) => i.includes(e))
        ) {
          continue;
        }

        const keys = i.split("~").map((e) => e.trim());
        keys.reduce((acc: Record<string, unknown>, key, idx) => {
          return (
            acc[key] || (acc[key] = idx === keys.length - 1 ? data[i] : {})
          );
        }, result);
      }

      return result;
    }

    categorySpecificScoreFlattened[subcategory] = categoryScore;

    const unflattenedSectionScore = unflatten(
      flattenedScoreTable,
    ) as unknown as InferenceType;
    //console.info({ categoryScore, flattenedScoreTable });
    return {
      unflattenedSectionScore,
      categoryScore: categoryScore,
      subcategory,
      categorySpecificScoreFlattened: Object.fromEntries(
        Object.entries(categorySpecificScoreFlattened).map(([key, value]) => {
          const nonZeroValues = Object.fromEntries(
            Object.entries(value).filter(([, value]) => value !== 0),
          );
          return [key, nonZeroValues];
        }),
      ),
    };
  };

  const processUIInference = (message: any) => {
    const {
      sectionInfo,
      sectionScores,
      flattenedScoreTable,
      categorySpecificScoreFlattened,
    } = message.payload;

    if (!sectionInfo) {
      message.resolve({
        inference: null,
        topInferredAttributes: {},
      });
      return;
    }

    const sectionIds = Object.keys(sectionInfo);
    let inference: InferenceType | null = null;
    // const flattenedScoreTable: Record<string, number> = {};
    // const categorySpecificScoreFlattened: Record<
    //   string,
    //   Record<string, number>
    // > = {};

    const categorySpecificScore = {};
    let categoryScoreFinal = {};
    let changedCategorySpecificScoreFlattened = {};

    sectionIds.forEach((sectionId) => {
      const sectionData = sectionInfo[sectionId];
      const score = sectionScores[sectionId] ?? 0;

      const {
        unflattenedSectionScore: inferenceScore,
        categoryScore,
        subcategory,
        categorySpecificScoreFlattened: categorySpecificScoreFlattenedFinal,
      } = getScoreTable(
        sectionData,
        score,
        flattenedScoreTable,
        categorySpecificScoreFlattened,
      );

      changedCategorySpecificScoreFlattened =
        categorySpecificScoreFlattenedFinal;

      categoryScoreFinal = categoryScore;

      if (Object.keys(categoryScore).length === 0) {
        return;
      }

      inference = inferenceScore;

      categorySpecificScore[subcategory] =
        unflatten(categoryScore)?.sectionInferenceAttributes;
    });

    const topInferredAttributes: ProductInferenceAttributes = {};

    Object.keys(
      (inference?.sectionInferenceAttributes as SectionInferenceAttributes) ??
        {},
    ).forEach((key) => {
      const topAttribute = Object.entries(
        inference?.sectionInferenceAttributes?.[key] ?? {},
      )
        .filter(([, value]) => !!value)
        .sort(([, a], [, b]) => b - a)
        .map(([attr]) => attr)
        .slice(0, 5);

      topInferredAttributes[key] = topAttribute;
    });

    message.resolve({
      inference,
      topInferredAttributes,
      categorySpecificScore,
      categoryScore: categoryScoreFinal,
      categorySpecificScoreFlattenedFinal:
        changedCategorySpecificScoreFlattened,
    });
  };

  onMessage((message) => {
    processUIInference(message);
  });

  onMessage("processUIInference", processUIInference);
});
